import {
  HttpBackend,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthActions, AuthState } from '@qtek/libs/store';
import { HTTP_BASE_URL, TASK_SERVER_BASE_URL } from '@qtek/shared/models';
import { checkRequestMetadata } from '@qtek/shared/utils';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, share, switchMap } from 'rxjs/operators';

import { QtHttpInterceptor } from '../qtek-interceptor';

@Injectable()
export class RevalidateInterceptor implements QtHttpInterceptor {
  private baseUrl = inject(HTTP_BASE_URL);
  private taskServerUrl = inject(TASK_SERVER_BASE_URL);
  private backend = inject(HttpBackend);
  private router = inject(Router);
  private store = inject<Store<AuthState>>(Store);
  private route = inject(ActivatedRoute);

  readonly qtek = true;

  revalidateRequest = new HttpRequest(
    'GET',
    this.baseUrl + '/api/v2/auth/refresh',
    undefined,
    {
      withCredentials: true,
    }
  );

  /**
   * Sometimes we send few request in short period. If user is not authenticated
   * then every requests will trigger revalidate
   */
  pendingRequest: null | Observable<HttpEvent<any>> = null;

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (
      !this.isBackendCall(req.url) ||
      req.url.includes('/api/v2/auth/logout')
    ) {
      return next.handle(req);
    }

    if (
      !this.isBackendCall(req.url) ||
      req.url.includes('/api/v2/auth/refresh') ||
      req.headers.has('Authorization')
    ) {
      return next.handle(req);
    }

    const { newReq, hasMetadata: skipRevalidateErrorHandling } =
      checkRequestMetadata(req, 'X-Skip-Revalidate-Error-Handling');

    return next.handle(newReq).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(
            newReq,
            next,
            skipRevalidateErrorHandling,
            error
          );
        }
        return skipRevalidateErrorHandling
          ? throwError(() => error)
          : throwError(() => new Error(error.error));
      })
    );
  }

  // The refreshed `at` cookie is scoped to the parent domain, so it covers the task server too.
  private isBackendCall(url: string): boolean {
    return (
      url.startsWith(this.baseUrl) ||
      (!!this.taskServerUrl && url.startsWith(this.taskServerUrl))
    );
  }

  handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    skipRevalidateErrorHandling: boolean,
    error: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    if (this.pendingRequest === null) {
      this.pendingRequest = this.backend.handle(this.revalidateRequest).pipe(
        filter(event => event.type === HttpEventType.Response),
        share()
      );
    }
    return this.pendingRequest.pipe(
      /**
       * This `catchError` sits on the refresh observable, not on the retried
       * request. Only a failed refresh may log the user out — an error from the
       * retried request must propagate as an ordinary HTTP error.
       */
      catchError(response => {
        this.pendingRequest = null;
        /**
         * Error handling for 400 status code in revalidation
         * request can be skipped with this flag. This is used primarely
         * in {@link AuthGuard} to get correct returnUrl from router.
         *
         * Attempt to get returnUrl in this interceptor will fail(return url will be empty string)
         * when user just started application on secured view, and he is not authenticated.
         */
        if (skipRevalidateErrorHandling) {
          return throwError(() => new Error(response));
        }

        /**
         * Revalidate requests failed. This means that some request to API
         * returned 401 status code and user have no revalidation token either.
         *
         * In this case user is not allowed to any secured route and should
         * be logged out(to restart any state that may be present). In addition
         * returnUrl query parametr should be set, so when user log in again,
         * it can start when he finished. It only works if user was authenticated,
         * but cookie expired.
         */
        const state = this.router.routerState.snapshot;
        const returnUrl = state.url !== '/' ? state.url : '';
        const queryParams = returnUrl ? { returnUrl } : null;
        const currentParams = this.route.snapshot.queryParams || {};
        // debugger;
        this.store.dispatch(
          AuthActions.logout({
            queryParams: { ...queryParams, ...currentParams },
          })
        );

        /**
         * Rethrow original error.
         */
        return throwError(() => new Error(error.error));
      }),
      switchMap(() => {
        this.pendingRequest = null;
        return next.handle(request);
      })
    );
  }
}
